export default class RatingExplainer {
    
    constructor(rating) {
        this.rating = rating;
    }

    // Get full explanation of all conditions
    getFullExplanation() {
        const explanations = [
            this.getWaveHeightExplanation(),
            this.getSwellPeriodExplanation(),
            this.getSwellDirectionExplanation(),
            this.getWindSpeedExplanation(),
            this.getWindDirectionExplanation()
        ].filter(exp => exp !== null);

        return explanations.join(' ');
    }

    // Get explanation for wave height
    getWaveHeightExplanation() {
        const { waveheight } = this.rating.conditions;
        const { preferred_wave_height_min, preferred_wave_height_max } = this.rating.ideal;
        const score = this.rating.waveheight;

        if (waveheight === null || waveheight === undefined || 
            preferred_wave_height_min === null || preferred_wave_height_max === null) {
            return null;
        }

        const wave = Number(waveheight);
        const minWave = Number(preferred_wave_height_min);
        const maxWave = Number(preferred_wave_height_max);

        if (score === 3) {
            return `Wave height is ${wave}m, perfectly within your ideal range of ${minWave}m-${maxWave}m.`;
        } else if (score === 2) {
            if (wave < minWave) {
                return `Wave height is ${wave}m, slightly below your minimum of ${minWave}m but still surfable.`;
            } else {
                return `Wave height is ${wave}m, slightly above your maximum of ${maxWave}m but still manageable.`;
            }
        } else if (score === 1) {
            if (wave < minWave) {
                return `Wave height is ${wave}m, noticeably smaller than your preferred ${minWave}m-${maxWave}m range.`;
            } else {
                return `Wave height is ${wave}m, noticeably larger than your preferred ${minWave}m-${maxWave}m range.`;
            }
        } else {
            if (wave < minWave) {
                return `Wave height is ${wave}m, too small compared to your ideal ${minWave}m-${maxWave}m.`;
            } else {
                return `Wave height is ${wave}m, too large compared to your ideal ${minWave}m-${maxWave}m.`;
            }
        }
    }

    // Get explanation for swell period
    getSwellPeriodExplanation() {
        const { swellperiod, swelldirection } = this.rating.conditions;
        const { preferred_swell_period_min, preferred_swell_period_max, preferred_swell_direction } = this.rating.ideal;
        const score = this.rating.swellperiod;

        if (swellperiod === null || swellperiod === undefined || 
            preferred_swell_period_min === null || preferred_swell_period_max === null) {
            return null;
        }

        const period = Number(swellperiod);
        const minPeriod = Number(preferred_swell_period_min);
        const maxPeriod = Number(preferred_swell_period_max);

        // Check if direction is affecting the score
        let directionNote = '';
        if (swelldirection !== null && swelldirection !== undefined && 
            preferred_swell_direction !== null && preferred_swell_direction !== undefined) {
            const swellDir = Number(swelldirection);
            const idealDir = Number(preferred_swell_direction);
            let diff = Math.abs(swellDir - idealDir);
            if (diff > 180) diff = 360 - diff;
            
            if (diff > 45 && period >= minPeriod && period <= maxPeriod) {
                directionNote = ' However, the swell direction is significantly off, reducing the benefit of this good period.';
            }
        }

        if (score === 3) {
            return `Swell period is ${period}s, ideal for your preference of ${minPeriod}s-${maxPeriod}s.`;
        } else if (score === 2) {
            if (period < minPeriod) {
                return `Swell period is ${period}s, slightly shorter than your ideal ${minPeriod}s-${maxPeriod}s but still decent.`;
            } else {
                return `Swell period is ${period}s, slightly longer than your ideal ${minPeriod}s-${maxPeriod}s but still good.`;
            }
        } else if (score === 1) {
            if (directionNote) {
                return `Swell period is ${period}s, within your ideal range.${directionNote}`;
            }
            if (period < minPeriod) {
                return `Swell period is ${period}s, notably shorter than your preferred ${minPeriod}s-${maxPeriod}s range.`;
            } else {
                return `Swell period is ${period}s, notably longer than your preferred ${minPeriod}s-${maxPeriod}s range.`;
            }
        } else {
            if (period < minPeriod) {
                return `Swell period is ${period}s, too short for your ideal ${minPeriod}s-${maxPeriod}s.${directionNote}`;
            } else {
                return `Swell period is ${period}s, too long for your ideal ${minPeriod}s-${maxPeriod}s.${directionNote}`;
            }
        }
    }

    // Get explanation for swell direction
    getSwellDirectionExplanation() {
        const { swelldirection } = this.rating.conditions;
        const { preferred_swell_direction } = this.rating.ideal;
        const score = this.rating.swelldirection;

        if (swelldirection === null || swelldirection === undefined || 
            preferred_swell_direction === null || preferred_swell_direction === undefined) {
            return null;
        }

        const swellDir = Number(swelldirection);
        const idealDir = Number(preferred_swell_direction);
        let diff = Math.abs(swellDir - idealDir);
        if (diff > 180) diff = 360 - diff;

        const swellDirText = this.getDirectionText(swellDir);
        const idealDirText = this.getDirectionText(idealDir);

        if (score === 3) {
            return `Swell direction is ${swellDirText} (${swellDir}°), spot-on with your ideal ${idealDirText} (${idealDir}°).`;
        } else if (score === 2) {
            return `Swell direction is ${swellDirText} (${swellDir}°), close to your ideal ${idealDirText} (${idealDir}°), about ${Math.round(diff)}° off.`;
        } else if (score === 1) {
            return `Swell direction is ${swellDirText} (${swellDir}°), somewhat off from your preferred ${idealDirText} (${idealDir}°) by ${Math.round(diff)}°.`;
        } else {
            return `Swell direction is ${swellDirText} (${swellDir}°), significantly different from your ideal ${idealDirText} (${idealDir}°), ${Math.round(diff)}° off.`;
        }
    }

    // Get explanation for wind speed
    getWindSpeedExplanation() {
        const { windspeed, winddirection } = this.rating.conditions;
        const { preferred_wind_speed_min, preferred_wind_speed_max, preferred_wind_direction } = this.rating.ideal;
        const score = this.rating.windspeed;

        if (windspeed === null || windspeed === undefined || 
            preferred_wind_speed_min === null || preferred_wind_speed_max === null) {
            return null;
        }

        const speed = Number(windspeed);
        const minSpeed = Number(preferred_wind_speed_min);
        const maxSpeed = Number(preferred_wind_speed_max);

        // Check if direction is affecting the score
        let directionNote = '';
        if (winddirection !== null && winddirection !== undefined && 
            preferred_wind_direction !== null && preferred_wind_direction !== undefined) {
            const windDir = Number(winddirection);
            const idealDir = Number(preferred_wind_direction);
            let diff = Math.abs(windDir - idealDir);
            if (diff > 180) diff = 360 - diff;
            
            if (diff > 95 && speed >= minSpeed && speed <= maxSpeed) {
                directionNote = ' However, the wind direction is unfavorable, greatly reducing the quality.';
            }
        }

        if (score === 3) {
            return `Wind speed is ${speed} km/h, perfect for your preference of ${minSpeed}-${maxSpeed} km/h.`;
        } else if (score === 2) {
            if (speed < minSpeed) {
                return `Wind speed is ${speed} km/h, slightly lighter than your ideal ${minSpeed}-${maxSpeed} km/h.`;
            } else {
                return `Wind speed is ${speed} km/h, slightly stronger than your ideal ${minSpeed}-${maxSpeed} km/h.`;
            }
        } else if (score === 1) {
            if (directionNote) {
                return `Wind speed is ${speed} km/h, within your ideal range.${directionNote}`;
            }
            if (speed < minSpeed) {
                return `Wind speed is ${speed} km/h, noticeably lighter than your preferred ${minSpeed}-${maxSpeed} km/h.`;
            } else {
                return `Wind speed is ${speed} km/h, noticeably stronger than your preferred ${minSpeed}-${maxSpeed} km/h.`;
            }
        } else {
            if (speed < minSpeed) {
                return `Wind speed is ${speed} km/h, too light compared to your ideal ${minSpeed}-${maxSpeed} km/h.${directionNote}`;
            } else {
                return `Wind speed is ${speed} km/h, too strong compared to your ideal ${minSpeed}-${maxSpeed} km/h.${directionNote}`;
            }
        }
    }

    // Get explanation for wind direction
    getWindDirectionExplanation() {
        const { winddirection, windspeed } = this.rating.conditions;
        const { preferred_wind_direction } = this.rating.ideal;
        const score = this.rating.winddirection;

        if (winddirection === null || winddirection === undefined || 
            preferred_wind_direction === null || preferred_wind_direction === undefined) {
            return null;
        }

        const windDir = Number(winddirection);
        const idealDir = Number(preferred_wind_direction);
        const speed = Number(windspeed || 0);
        let diff = Math.abs(windDir - idealDir);
        if (diff > 180) diff = 360 - diff;

        const windDirText = this.getDirectionText(windDir);
        const idealDirText = this.getDirectionText(idealDir);

        if (speed <= 8) {
            return `Wind direction is ${windDirText} (${windDir}°) with light winds (${speed} km/h), so direction has minimal impact.`;
        }

        if (score === 3) {
            return `Wind direction is ${windDirText} (${windDir}°), excellent match with your ideal ${idealDirText} (${idealDir}°).`;
        } else if (score === 2) {
            return `Wind direction is ${windDirText} (${windDir}°), fairly close to your ideal ${idealDirText} (${idealDir}°), about ${Math.round(diff)}° off.`;
        } else if (score === 1) {
            return `Wind direction is ${windDirText} (${windDir}°), somewhat off from your preferred ${idealDirText} (${idealDir}°) by ${Math.round(diff)}°.`;
        } else {
            return `Wind direction is ${windDirText} (${windDir}°), poor for your ideal ${idealDirText} (${idealDir}°), ${Math.round(diff)}° off.`;
        }
    }

    // Get overall score explanation
    getScoreExplanation() {
        const score = this.rating.score;
        const points = this.rating.points;
        const maxPoints = this.rating.maxPoints;

        if (score >= 4.5) {
            return `Outstanding conditions! Scoring ${score}/5 stars (${points}/${maxPoints} points). Nearly perfect alignment with your preferences.`;
        } else if (score >= 4.0) {
            return `Excellent conditions! Scoring ${score}/5 stars (${points}/${maxPoints} points). Very close to your ideal setup.`;
        } else if (score >= 3.0) {
            return `Good conditions. Scoring ${score}/5 stars (${points}/${maxPoints} points). Decent match with your preferences.`;
        } else if (score >= 2.0) {
            return `Fair conditions. Scoring ${score}/5 stars (${points}/${maxPoints} points). Some factors are off from your ideal.`;
        } else if (score >= 1.0) {
            return `Marginal conditions. Scoring ${score}/5 stars (${points}/${maxPoints} points). Most factors don't match your preferences.`;
        } else {
            return `Poor conditions. Scoring ${score}/5 stars (${points}/${maxPoints} points). Conditions are far from your ideal.`;
        }
    }

    // Helper method to convert degrees to compass direction
    getDirectionText(degrees) {
        const directions = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
        const index = Math.round(degrees / 22.5) % 16;
        return directions[index];
    }
}
